import { computed } from 'mobx';

import { KeyApiClient } from '../../api/key-api-client';
import {
    ICustomerKey,
    IDistributeKeyRequestOptions,
    IDistributeKeyResponseResult,
    IUploadCustomerKeyOptions,
    KeyType,
    PSP,
    IClientPspEncryptionType,
    IUploadCustomerKeyResponse,
    KcvPspKeyDto,
    IKeyTypeSupport,
} from '../../dto/key-management/key-dto';

import { AssureBaseStore, IAssureStoreConstructorOptions } from '../assure-base.store';

import { formatDate } from 'common-utils/date-utils';
import { getErrorMessage } from 'common-utils';

export interface IDisplayKey {
    id: number;
    psp: PSP;
    keyId: number;
    keyName: string;
    partenOrClient: string;
    dateUploaded: string | Date;
    dateUploadedFormatted: string;
}

export class KeyStore extends AssureBaseStore<KeyApiClient, ICustomerKey> {
    public constructor(options: IAssureStoreConstructorOptions) {
        super(options);
    }

    protected get apiClient(): KeyApiClient {
        return this.apiClientStore.apiClients.key;
    }

    public async loadKeys(force?: boolean): Promise<void> {
        if (!force && this.hasEntities()) return;
        try {
            this.setDataLoading(true);
            this.setError(undefined);
            this.setEntities(await this.apiClient.getInventoryKeys());
        } catch (err) {
            this.setError(getErrorMessage(err));
        } finally {
            this.setDataLoading(false);
        }
    }

    @computed
    public get displayKeys(): IDisplayKey[] {
        if (!this.hasEntities()) return [];
        const clientKeys = this.entities.filter(item => item.clientAccount);
        const clientToKeySetID = clientKeys.reduce((map, item) => {
            if (item.keyType == KeyType.KSID) {
                map.set(item.clientAccount.id, [...map.get(item.clientAccount.id) || [], { ksid: item.id, psp: item.psp }]);
            }
            return map;
        }, new Map<number, { ksid: number; psp: PSP }[]>());

        return clientKeys.map((key) => {
            const clientKeySets = clientToKeySetID.get(key.clientAccount?.id) || [];
            return {
                id: key.id,
                psp: key.psp,
                keyId: clientKeySets.find(item => item.psp == key.psp)?.ksid,
                keyName: key.keyName,
                partenOrClient: key.clientAccount?.name,
                dateUploaded: key.dateUploaded,
                dateUploadedFormatted: formatDate(key.dateUploaded.toLocaleString()),
            };
        });
    }

    public async createKeys(keyUpload: IUploadCustomerKeyOptions, configFiles?: File[]): Promise<IUploadCustomerKeyResponse> {
        const result = await this.apiClient.createKeys(keyUpload, configFiles);
        if (result.created) {
            await this.loadKeys(true);
        }
        return result;
    }

    public async deleteKey(id: number): Promise<boolean> {
        const result = await this.apiClient.deleteKey(id);
        if (result) {
            this.removeEntity(id);
        }
        return result;
    }

    public distributeKey(options: IDistributeKeyRequestOptions): Promise<{ result: IDistributeKeyResponseResult[] }> {
        return this.apiClient.distributeKey(options);
    }

    public async getKcvPspKeys(account: string, psp: string): Promise<KcvPspKeyDto> {
        return new KcvPspKeyDto(await this.apiClient.getKcvPspKeys(account, psp));
    }

    public async getPspEncryptionTypesOfAccount(accountUuid: string, fullyKeyConfigured?: boolean): Promise<IClientPspEncryptionType[]> {
        return this.apiClient.getPspEncryptionTypesOfAccount(accountUuid, fullyKeyConfigured);
    }

    public getKmsAccountsWithValidPspData(): Promise<{ accountUuids: string[] }> {
        return this.apiClient.getKmsAccountsWithValidPspData();
    }

    public getKmsAccountUuidsByPspKeyTypes(pspKeyTypes: PSP[]): Promise<{ accountUuids: string[] }> {
        return this.apiClient.getKmsAccountUuidsByPspKeyTypes(pspKeyTypes);
    }

    public async getSupportedKeyTypes(): Promise<{ [key in string]: IKeyTypeSupport[] }> {
        return this.apiClient.getSupportedKeyTypes();
    }
}
